import { IconButton, Drawer, Stack, Typography, Divider, Slide, Box, Alert } from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import _isEqual from 'lodash/isEqual';
import { Intersect } from '@treedis/tour-loader/dist/sdk-components/SceneComponent';
import { TSceneComponent, TourLoaderEventsType, iTHREE } from '@treedis/tour-loader';

import { toast } from 'react-toastify';
import { ThreadsItems } from './components/ThreadsItems';
import { Thread } from './components/Thread';
import DeviceManager from '@/components/util/deviceManager';
import { ThreadLoginPopup, getTokenData } from './components/modals/ThreadLoginPopup';
import socket from '@/components/util/socket';
import { formData } from '@/utils/formData';
import PublicAPI from '@/api/TreedisPublicDAO';
import { EditMenu } from './components/EditMenu';

export enum NoteEditEventType {
  EditPosition = 'editPosition',
  EditComplition = 'editComplition',
}

export const Threads = ({
  isOpen,
  closeDrawer,
  showCaseTemp,
  showcaseTag,
  iframe,
  initialThreads,
  tour,
  appState,
  addThreadToSpace,
  showcase,
  selectedThreadProp,
  incrementNewMessagesCount,
  decrementNewMessagesCount,
  resetNewMessagesCount,
  removeThreadFromSpace,
  toggleThreadVisibility,
  hideElements,
  openDrawer,
  moveToTag,
  getLocation,
  isRtl,
}: {
  isOpen: boolean;
  closeDrawer: () => void;
  openDrawer: () => void;
  showCaseTemp: any;
  iframe: any;
  initialThreads: any;
  tour: any;
  appState: any;
  addThreadToSpace: (thread: any) => Promise<void>;
  showcase: any;
  showcaseTag: any;
  selectedThreadProp: any;
  incrementNewMessagesCount: () => void;
  decrementNewMessagesCount: () => void;
  resetNewMessagesCount: () => void;
  removeThreadFromSpace: (thread: any) => Promise<void>;
  toggleThreadVisibility: (thread: any, isVisible: boolean) => void;
  hideElements: (data: any) => Promise<void>;
  moveToTag: (tagSid: any) => void;
  getLocation: () => string;
  isRtl: boolean;
}) => {
  const [newThread, setNewThread] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [newThreadMessages, setNewThreadMessages] = useState<{ [key: number]: number }>({});
  const [threads, setThreads] = useState(initialThreads);
  const [selectedThread, setSelectedThread] = useState(null);
  const [addThreadClicked, setAddThreadClicked] = useState(false);
  const [activeStep, setActiveStep] = useState(1);
  const [userId, setUserId] = useState(null);
  const [isAdding, setIsAdding] = useState(false);
  const [filteredCount, setFilteredCount] = useState(0);
  const [isGuest, setIsGuest] = useState(false);
  const [collaborators, setCollaborators] = useState(tour?.users);
  const isSAPActive = useMemo(() => tour.isSAPActive, [tour.isSAPActive]);
  const isTourAdmin = useMemo(() => tour.userId === userId, [tour.userId, userId]);

  const [mediaUrls, setMediaUrls] = useState<{ src: string; flaggedToDelete: boolean }[]>([]);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [openedMenuId, setOpenedMenuId] = useState(null);
  const [isDeleteNoteModalOpen, setIsDeleteNoteModalOpen] = useState(false);
  const isMenuOpen = Boolean(anchorEl);

  const controls = useRef<TSceneComponent | null>(null);
  const tagNode = useRef<any>();
  const inputNodeComponent = useRef<any>();
  const attachToCursor = useRef<any>(false);
  const normal = useRef<any>();
  const clickListenerRef = useRef<any>();
  const contentDrawerRef = useRef<any>();
  const refUserId = useRef<number>();
  const initialThreadsRef = useRef<any>([]);

  const updateMessages = (thread, messageToUpdate) => {
    const { messages } = thread;
    const messageIndex = messages.findIndex((message) => message?.id === messageToUpdate.id);

    if (messageIndex !== -1) {
      messages[messageIndex] = messageToUpdate;
    } else {
      messages.push(messageToUpdate);
    }

    return messages;
  };

  useEffect(() => {
    if (_isEqual(initialThreads, initialThreadsRef.current)) return;
    setThreads([...initialThreads]);
    initialThreadsRef.current = initialThreads;
  }, [initialThreads]);

  useEffect(() => {
    setCollaborators(tour.users);
    setIsGuest(false);
  }, [tour]);

  const handleEditMessage = () => {
    const foundMessage = selectedThread?.messages.find((m) => m.id === openedMenuId);
    if (foundMessage) {
      setEditMode(true);
      setMediaUrls(foundMessage.mediaUrls.map((url) => ({ src: url, flaggedToDelete: false })));
    }
    setAnchorEl(null);
  };

  const handleIncrementNewMessagesCount = (newThreadMessageUserId: number, newThreadMessageThreadId: number) => {
    if (newThreadMessageUserId === refUserId.current) return;
    incrementNewMessagesCount();
    setNewThreadMessages((prevMessages) => {
      if (prevMessages[newThreadMessageThreadId]) {
        // eslint-disable-next-line operator-assignment
        prevMessages[newThreadMessageThreadId] += 1;
      } else {
        prevMessages = {
          ...prevMessages,
          [newThreadMessageThreadId]: 1,
        };
      }
      return prevMessages;
    });
  };

  const handleDecrementNewMessagesCount = (newThreadMessageUserId: number, newThreadMessageThreadId: number) => {
    if (newThreadMessageUserId === refUserId.current) return;
    decrementNewMessagesCount();
    setNewThreadMessages((prevMessages) => {
      if (prevMessages[newThreadMessageThreadId]) {
        // eslint-disable-next-line operator-assignment
        prevMessages[newThreadMessageThreadId] -= 1;
      }
      return prevMessages;
    });
  };

  const backToThreadsStep = () => {
    setActiveStep(1);
    setSelectedThread(null);
    setEditMode(false);
    contentDrawerRef?.current?.scrollTo && contentDrawerRef.current.scrollTo(0, 0);
  };

  useEffect(() => {
    socket.emit('thread_join', { room: tour.slug });

    socket.on('thread_added', async (data) => {
      const { newThread: threadFromAnotherUser } = data;

      threadFromAnotherUser.customIcon = 'thread';

      await addThreadToSpace(threadFromAnotherUser);
      setThreads((prevThreads) => [...prevThreads, threadFromAnotherUser]);
      incrementNewMessagesCount();
    });

    socket.on('thread_edit', async (data) => {
      const { savedThread, eventType } = data;

      if (eventType === NoteEditEventType.EditPosition) {
        if (data.isRecentNote) {
          await showCaseTemp.tag.edit(savedThread);
        } else {
          const foundThread = initialThreadsRef.current.find(
            (thread) =>
              thread.defaultSid === savedThread.defaultSid || thread.sid === savedThread.defaultSid || thread.defaultSid === savedThread.sid || thread.id === savedThread.id,
          );
          if (foundThread?.sid) {
            await showcaseTag.editPosition(foundThread.sid, {
              anchorPosition: {
                x: savedThread.positionX,
                y: savedThread.positionY,
                z: savedThread.positionZ,
              },
            });

            setThreads((prevThread) => {
              return prevThread.map((thread) => {
                if (thread.id === savedThread.id) {
                  return {
                    ...thread,
                    positionX: savedThread.positionX,
                    positionY: savedThread.positionY,
                    positionZ: savedThread.positionZ,
                  };
                }
                return thread;
              });
            });
          }
        }
      }

      if (eventType === NoteEditEventType.EditComplition) {
        await handleCompleteNote(savedThread.id, savedThread);
      }
    });

    socket.on('message_added', (data) => {
      let updatedThread;
      setThreads((prevThreads) => {
        const foundThread = prevThreads.find((thread) => thread.id === data.newThreadMessage.threadId);
        if (foundThread) {
          return prevThreads.map((thread) => {
            if (thread.id === foundThread.id) {
              const messages = updateMessages(thread, data.newThreadMessage);
              updatedThread = { ...thread, messages };
              return updatedThread;
            }
            return thread;
          });
        }
        return prevThreads;
      });
      setSelectedThread((prevThread) => {
        if (updatedThread && (updatedThread?.id === prevThread?.id || !prevThread)) {
          return updatedThread;
        }
        return prevThread;
      });

      handleIncrementNewMessagesCount(data.newThreadMessage.userId, data.newThreadMessage.threadId);
    });

    socket.on('message_edited', (data) => {
      let updatedThread;
      setThreads((prevThreads) => {
        const foundThread = prevThreads.find((thread) => thread.id === data.updatedMessage.threadId);
        if (foundThread) {
          return prevThreads.map((thread) => {
            if (thread.id === foundThread.id) {
              const messages = updateMessages(thread, data.updatedMessage);
              updatedThread = { ...thread, title: data.updatedMessage.savedTitle, messages };
              return updatedThread;
            }
            return thread;
          });
        }
        return prevThreads;
      });
      setSelectedThread((prevThread) => {
        if (updatedThread && (updatedThread?.id === prevThread?.id || !prevThread)) {
          return updatedThread;
        }
        return prevThread;
      });
    });

    socket.on('message_deleted', (data) => {
      setThreads((prevThreads) => {
        const foundThread = prevThreads.find((thread) => thread.id === data.data.threadId);
        if (foundThread) {
          return prevThreads.map((thread) => {
            if (thread.id === foundThread.id) {
              const messages = thread.messages.filter((message) => message.id !== data.data.id);
              return { ...thread, messages };
            }
            return thread;
          });
        }
        return prevThreads;
      });
      setSelectedThread((prevThread) => {
        if (prevThread?.id === data.data.threadId) {
          const messages = prevThread.messages.filter((message) => message.id !== data.data.id);
          return { ...prevThread, messages };
        }
        return prevThread;
      });

      handleDecrementNewMessagesCount(data.data.userId, data.data.threadId);
    });

    socket.on('thread_delete', async (data) => {
      const { deletedThread } = data;
      await removeThreadFromSpace(deletedThread);

      await setThreads((prevState) => {
        const filteredThreads = prevState.filter((thread) => thread.id !== deletedThread.id);
        return filteredThreads;
      });
      backToThreadsStep();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // find clicked thread from tag click
  useEffect(() => {
    if (selectedThreadProp) {
      const foundThread = threads.find((thread) => thread.id === selectedThreadProp.id);
      if (foundThread) setSelectedThread(foundThread);
    }
  }, [selectedThreadProp]);

  const configureScene = useCallback(async () => {
    await showCaseTemp.tag.configureScene(controls);

    showCaseTemp.tag.pointerIntersection(tagNode, attachToCursor);
  }, [showCaseTemp.tag]);

  useEffect(() => {
    if (appState === TourLoaderEventsType.PLAYING) {
      configureScene();
    }
  }, [appState, configureScene]);

  useEffect(() => {
    refUserId.current = userId;
  }, [userId]);

  const getThree = async () => {
    const THREE = await iTHREE();
    normal.current = new THREE.Vector3(0, 1, 0);
  };

  useEffect(() => {
    getThree();
  }, []);

  const addTag = useCallback(
    async (cb: (position: { x: number; y: number; z: number }, stemVector: { x: number; y: number; z: number }) => void) => {
      if (!showCaseTemp.tag) {
        return;
      }

      attachToCursor.current = true;

      await showCaseTemp.tag.initTagEditObject({
        tagNode,
        inputNodeComponent,
        controls,
        options: {},
        userNavigationEnabled: false,
      });

      showCaseTemp.mpSDK.Pointer.intersection.subscribe((e: Intersect) => {
        const { x, y, z } = e.normal;
        if (normal.current?.isVector3) normal.current.set(x, y, z);
      });

      const clickListener = async () => {
        iframe.contentWindow.document.removeEventListener('click', clickListener);
        iframe.contentWindow.document.removeEventListener('touchend', clickListener);
        if (inputNodeComponent.current && tagNode.current) {
          inputNodeComponent.current.inputs.userNavigationEnabled = true;
          inputNodeComponent.current.inputs.eventsEnabled = false;
          inputNodeComponent.current.inputs.unfiltered = true;
          const position = tagNode.current.obj3D?.position?.clone();
          const stemVector = normal.current
            ? normal.current.clone().clampLength(0, 0.3)
            : {
                x: 0,
                y: 0.3,
                z: 0,
              };

          tagNode.current.stop();
          tagNode.current = undefined;
          inputNodeComponent.current = undefined;

          cb(position, stemVector);
        }
      };
      clickListenerRef.current = clickListener;
      iframe.contentWindow.document.addEventListener('click', clickListener, {
        once: true,
        capture: true,
      });
      iframe.contentWindow.document.addEventListener('touchend', clickListener, {
        once: true,
        capture: true,
      });
    },

    [iframe.contentWindow.document, showCaseTemp.mpSDK.Pointer.intersection, showCaseTemp.tag],
  );

  const handleAddingThread = async () => {
    setIsAdding(true);
    closeDrawer();

    await addTag(async (position, stemVector) => {
      const threadToAdd = {
        id: `temp-id-${Math.random()}`,
        sid: `temp-sid-${Math.random()}`,
        positionX: position.x,
        positionY: position.y,
        positionZ: position.z,
        stemVectorX: stemVector.x,
        stemVectorY: stemVector.y,
        stemVectorZ: stemVector.z,
        mpTitle: false,
        tooltip: false,
        stemVisible: true,
        mpNavigation: true,
        enabled: true,
        iconUrl: `${process.env.REACT_APP_DOMAIN || 'http://localhost:5030'}/v1/public/icons/thread`,
        description: '',
      };

      const sid = await showCaseTemp.tag.add(threadToAdd);
      threadToAdd.sid = sid;
      if (showCaseTemp.tag && controls.current) {
        attachToCursor.current = false;

        showCaseTemp.tag.startTranslateControls({
          tag: threadToAdd,
          tagNode,
          inputNodeComponent,
          controls,
          cb: () => {
            const { x, y, z } = tagNode.current.obj3D.position;
            threadToAdd.positionX = x;
            threadToAdd.positionY = y;
            threadToAdd.positionZ = z;
            showCaseTemp.tag.edit(threadToAdd);
          },
        });
      }
      setNewThread(threadToAdd);
      setIsAdding(false);
    });
  };

  const handleAddThread = useCallback(() => {
    if (!userId) {
      setUserId(null);
      return;
    }
    setSelectedThread(null);
    setAddThreadClicked(true);
    handleAddingThread();
  }, [userId, handleAddingThread]);

  const handleCloseDrawer = async () => {
    controls.current.inputs.selection = null;

    if (newThread) {
      await showCaseTemp.tag.remove(newThread.sid);

      setNewThread(null);
    } else {
      if (tagNode.current) {
        tagNode.current.stop();
        tagNode.current = undefined;
      }
      if (inputNodeComponent.current) {
        inputNodeComponent.current = undefined;
      }
    }
    setSelectedThread(null);
    setAddThreadClicked(false);
    setIsAdding(false);
    setEditMode(false);
    closeDrawer();
  };

  useEffect(() => {
    if (selectedThread || newThread) {
      setActiveStep(2);
    } else {
      setActiveStep(1);
    }

    return () => {
      setActiveStep(1);
    };
  }, [newThread, selectedThread]);

  const handleThreadItemSelect = (thread) => {
    setSelectedThread(thread);
    setActiveStep(2);
    setNewThreadMessages((prevMessages) => ({ ...prevMessages, [thread.id]: 0 }));
    resetNewMessagesCount();
  };

  const mainMessage = useMemo(() => {
    return selectedThread?.messages?.find((message) => message?.isMainMessage) || null;
  }, [selectedThread]);

  useEffect(() => {
    const keyDownHandler = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        tagNode.current.stop();
        tagNode.current = undefined;
        inputNodeComponent.current = undefined;
        if (clickListenerRef?.current) {
          iframe.contentWindow.document.removeEventListener('click', clickListenerRef.current);
          clickListenerRef.current = null;
        }
        setIsAdding(false);
        setAddThreadClicked(false);
      }
    };

    if (isAdding) {
      document.addEventListener('keydown', keyDownHandler);
    } else {
      document.removeEventListener('keydown', keyDownHandler);
    }

    return () => {
      document.removeEventListener('keydown', keyDownHandler);
    };
  }, [iframe.contentWindow.document, isAdding]);

  useEffect(() => {
    const token = localStorage.getItem('publicUserToken');
    if (token) {
      const { userIdFromToken } = getTokenData(token);
      if (!userIdFromToken) {
        setUserId(null);
      }
      setUserId(Number(userIdFromToken));
    }
  }, [userId, setUserId]);

  useEffect(() => {
    if (newThread) {
      openDrawer();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newThread]);

  useEffect(() => {
    setIsGuest(false);
  }, []);

  useEffect(() => {
    if (isGuest || !userId) {
      hideElements({ hideTags: initialThreads, polygonsToHide: [], vsImagesToHide: [], vsVideosToHide: [], vsItemsToHide: [] });
      initialThreads.forEach((thread) => {
        toggleThreadVisibility(thread, false);
      });
    }
  }, [isGuest, userId, tour]);

  const handleCompleteNote = async (noteId, noteFromAnotherUser) => {
    const thread = threads.find((t) => t.id === noteId);

    if (!noteFromAnotherUser) {
      delete thread.message;
      delete thread.dummyObject;

      const dataToSave = formData.create({
        ...thread,
        isCompleted: !thread.isCompleted,
      });
      try {
        const result = await PublicAPI.saveThread({
          data: dataToSave,
        });
        if (result.message === 'Success') {
          const { savedThread } = result;
          toggleThreadVisibility(thread, !savedThread.isCompleted);
          const updatedThreads = threads.map((t) => (t.id === savedThread.id ? { ...t, isCompleted: savedThread.isCompleted } : t));
          setThreads(updatedThreads);
          setSelectedThread((prevSelectedThread) => (prevSelectedThread ? { ...prevSelectedThread, isCompleted: savedThread.isCompleted } : savedThread));
          toast.success(`Note marked as ${savedThread.isCompleted ? 'completed' : 'incomplete'}`);
          socket.emit('thread_edit', { ...savedThread, room: tour.slug, eventType: NoteEditEventType.EditComplition });
        }
      } catch (error) {
        toast.error(`Failed to mark Note as ${!thread.isCompleted ? 'completed' : 'incomplete'}`);
      }
    } else {
      setThreads((prevThreads) => {
        const foundThread = prevThreads.find(
          (thread) =>
            thread.defaultSid === noteFromAnotherUser.defaultSid ||
            thread.sid === noteFromAnotherUser.defaultSid ||
            thread.defaultSid === noteFromAnotherUser.sid ||
            thread.id === noteFromAnotherUser.id,
        );
        toggleThreadVisibility(foundThread.sid, noteFromAnotherUser.isCompleted);
        return prevThreads.map((t) => (t.id === noteFromAnotherUser?.id ? { ...t, isCompleted: noteFromAnotherUser?.isCompleted } : t));
      });
    }
  };

  const direction = tour.client?.rtl ? 'rtl' : '';
  return (
    <>
      {!isGuest && (
        <Stack className="no-reset no-reset-list">
          <Drawer
            open={isOpen}
            anchor="right"
            variant="persistent"
            ModalProps={{ keepMounted: true }}
            PaperProps={{
              sx: {
                maxWidth: DeviceManager.isMobile() || DeviceManager.isTablet() ? '416px' : '494px',
                width: '100%',
                background: 'transparent',
                overflowX: 'hidden',
                flexDirection: 'row-reverse',
                alignItems: 'center',
                border: 'none',
              },
            }}
            sx={{
              flexDirection: 'row-reverse',
              alignItems: 'center',
            }}
          >
            <Stack
              sx={{
                height: '100%',
                background: '#F7F7F7',
                maxWidth: DeviceManager.isMobile() || DeviceManager.isTablet() ? '394px' : '464px',
                width: '100%',
                borderLeft: '1px solid #EAECF0',
              }}
            >
              <Stack direction="row" justifyContent="space-between" alignItems="center" p={2} sx={{ direction, background: 'white' }}>
                {activeStep === 2 && selectedThread && (
                  <IconButton onClick={backToThreadsStep} sx={{ gap: 1, width: 'auto' }}>
                    {direction ? <i className="fa-solid fa-chevron-right" /> : <i className="fa-solid fa-chevron-left" style={{ color: '#101828', fontSize: '18px' }} />}
                  </IconButton>
                )}
                <Typography variant="body1" sx={{ fontWeight: 'bold', fontSize: '18px', font: 'Inter' }}>
                  {activeStep === 1 ? (
                    <>
                      Notes
                      <Box
                        component="div"
                        sx={{
                          backgroundColor: '#F2F4F7',
                          borderRadius: '50%',
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: '24px',
                          height: '24px',
                          textAlign: 'center',
                          marginLeft: '5px',
                        }}
                      >
                        <Box
                          component="span"
                          sx={{
                            color: '#2055FF',
                            fontSize: '14px',
                          }}
                        >
                          {filteredCount}
                        </Box>
                      </Box>
                    </>
                  ) : !!newThread ? (
                    'New note'
                  ) : (
                    ''
                  )}
                </Typography>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  {activeStep === 2 && selectedThread && (
                    <IconButton onClick={() => moveToTag(selectedThread.sid)} id={`target-button-${selectedThread.id}`}>
                      <i className="fa-regular fa-location-crosshairs" style={{ fontSize: '18px', fontWeight: 400, color: '#101828' }} />
                    </IconButton>
                  )}
                  {activeStep === 2 && selectedThread && mainMessage?.userId === userId && (
                    <EditMenu
                      selectedThread={selectedThread}
                      message={mainMessage}
                      setMediaUrls={setMediaUrls}
                      anchorEl={anchorEl}
                      setAnchorEl={setAnchorEl}
                      setOpenedMenuId={setOpenedMenuId}
                      isMenuOpen={isMenuOpen}
                      setEditMode={setEditMode}
                      setIsDeleteNoteModalOpen={setIsDeleteNoteModalOpen}
                      handleEdit={handleEditMessage}
                    />
                  )}
                  <IconButton onClick={handleCloseDrawer}>
                    <i className="fa-solid fa-xmark-large" style={{ fontSize: '18px', fontWeight: 400, color: '#101828' }} />
                  </IconButton>
                </Stack>
              </Stack>
              <Divider />

              <Stack direction="row" width="200%" sx={{ overflowX: 'hidden', padding: '0 !important', flexGrow: 1 }} ref={contentDrawerRef}>
                <Stack
                  sx={{
                    transform: activeStep === 1 ? 'translateX(0%)' : 'translateX(-100%)',
                    transition: 'transform 0.5s',
                    width: '100%',
                    direction,
                  }}
                >
                  <Stack gap={2} p={2}>
                    <ThreadsItems
                      rtl={tour.client?.rtl}
                      threads={threads}
                      setThreads={setThreads}
                      onThreadItemSelect={handleThreadItemSelect}
                      collaborators={collaborators}
                      userId={userId}
                      removeThreadFromSpace={removeThreadFromSpace}
                      tourSlug={tour?.slug}
                      showCaseTemp={showCaseTemp}
                      showcaseTag={showcaseTag}
                      isTourAdmin={isTourAdmin}
                      toggleThreadVisibility={toggleThreadVisibility}
                      hideElements={hideElements}
                      newThreadMessages={newThreadMessages}
                      controls={controls}
                      inputNodeComponent={inputNodeComponent}
                      tagNode={tagNode}
                      editMode={editMode}
                      setEditMode={setEditMode}
                      handleAddThread={handleAddThread}
                      moveToTag={moveToTag}
                      setFilteredCount={setFilteredCount}
                      onCompleteNote={handleCompleteNote}
                      isGuest={isGuest}
                    />
                  </Stack>
                </Stack>
                <Stack
                  gap={2}
                  sx={{
                    transform: activeStep === 1 ? 'translateX(100%)' : 'translateX(-100%)',
                    transition: 'transform 0.5s',
                    width: '100%',
                    direction,
                  }}
                >
                  <Thread
                    rtl={tour.client?.rtl}
                    selectedThread={selectedThread}
                    setSelectedThread={setSelectedThread}
                    updateMessages={updateMessages}
                    newThread={newThread}
                    setNewThread={setNewThread}
                    userId={userId}
                    tour={tour}
                    closeDrawer={() => {
                      closeDrawer();
                      setAddThreadClicked(false);
                      setNewThread(null);
                      controls.current.inputs.selection = null;
                      showCaseTemp.tag.stopPositionUpdates({ controls });
                    }}
                    setThreads={setThreads}
                    threads={threads}
                    showcase={showcase}
                    addThreadToSpace={addThreadToSpace}
                    showCaseTemp={showCaseTemp}
                    collaborators={collaborators}
                    isTourAdmin={isTourAdmin}
                    resetNewMessagesCount={() => {
                      if (selectedThread?.id > 0) {
                        setNewThreadMessages((prevMessages) => ({ ...prevMessages, [selectedThread.id]: 0 }));
                      }
                      resetNewMessagesCount();
                    }}
                    toggleThreadVisibility={toggleThreadVisibility}
                    onThreadItemSelect={handleThreadItemSelect}
                    setIsAdding={setIsAdding}
                    controls={controls}
                    isSAPActive={isSAPActive}
                    editMode={editMode}
                    setEditMode={setEditMode}
                    setAddThreadClicked={setAddThreadClicked}
                    onCompleteNote={handleCompleteNote}
                    onCloseDrawer={handleCloseDrawer}
                    removeThreadFromSpace={removeThreadFromSpace}
                    mediaUrls={mediaUrls}
                    setMediaUrls={setMediaUrls}
                    anchorEl={anchorEl}
                    setAnchorEl={setAnchorEl}
                    openedMenuId={openedMenuId}
                    setOpenedMenuId={setOpenedMenuId}
                    isMenuOpen={isMenuOpen}
                    isDeleteNoteModalOpen={isDeleteNoteModalOpen}
                    setIsDeleteNoteModalOpen={setIsDeleteNoteModalOpen}
                    handleEdit={handleEditMessage}
                    getLocation={getLocation}
                    isRtl={isRtl}
                    isGuest={isGuest}
                  />
                </Stack>
              </Stack>
            </Stack>
            <Box
              sx={{
                top: '50%',
                left: 0,
                zIndex: 9999,
              }}
            >
              <IconButton
                onClick={closeDrawer}
                sx={{
                  width: '20px',
                  height: '80px',
                  background: '#ffffff',
                  borderRadius: 0,
                  borderTopLeftRadius: '8px',
                  borderBottomLeftRadius: '8px',
                  '&:hover': {
                    background: '#f0f0f0',
                  },
                }}
              >
                <i className="fa-solid fa-chevron-right" style={{ fontSize: '16px', color: '#000000' }} />
              </IconButton>
            </Box>
          </Drawer>
          <Drawer
            open={!isOpen}
            anchor="right"
            variant="persistent"
            ModalProps={{ keepMounted: true }}
            PaperProps={{
              sx: {
                maxWidth: DeviceManager.isMobile() || DeviceManager.isTablet() ? '416px' : '494px',
                background: 'transparent',
                overflowX: 'hidden',
                overflowY: 'scroll',
                flexDirection: 'row-reverse',
                alignItems: 'center',
                border: 'none',
              },
            }}
            sx={{
              flexDirection: 'row-reverse',
              alignItems: 'center',
            }}
          >
            <div>
              <IconButton
                sx={{
                  right: 0,
                  width: '20px',
                  height: '80px',
                  background: '#ffffff',
                  borderRadius: 0,
                  borderTopLeftRadius: '8px',
                  borderBottomLeftRadius: '8px',
                  '&:hover': {
                    background: '#f0f0f0',
                  },
                }}
                onClick={openDrawer}
              >
                <i className="fa-solid fa-chevron-left" style={{ fontSize: '16px', color: '#000000' }} />
              </IconButton>
            </div>
          </Drawer>
          {!addThreadClicked && !editMode && !selectedThread && !isGuest && (
            <Box
              sx={{
                position: 'absolute',
                bottom: 20,
                left: '50%',
                transform: 'translateX(-50%)',
                zIndex: 1200,
                direction,
              }}
            >
              <Slide id="slide-button" direction="up" in={isOpen} mountOnEnter unmountOnExit>
                <IconButton
                  size="large"
                  sx={{
                    width: '60px',
                    height: '60px',
                    borderRadius: '50%',
                    color: '#2055ff',
                    background: DeviceManager.isMobile() || DeviceManager.isTablet() ? '#fff' : '#fff',
                    '&:hover': {
                      background: '#fff',
                    },
                    '&.Mui-disabled': {
                      color: '#808080',
                      background: '#fff',
                    },
                  }}
                  disabled={addThreadClicked || editMode}
                  onClick={handleAddThread}
                >
                  <i
                    className="fa-solid fa-plus"
                    style={{
                      fontSize: '36px',
                    }}
                  />
                </IconButton>
              </Slide>
            </Box>
          )}
        </Stack>
      )}

      <ThreadLoginPopup collaborators={collaborators} userId={userId} setUserId={setUserId} setIsGuest={setIsGuest} tour={tour} />
      {isAdding && (
        <Alert
          severity="info"
          sx={{
            zIndex: 9999,
            position: 'fixed',
            top: 10,
            left: 0,
            right: 0,
            py: 0,
            mx: 'auto',
            maxWidth: '230px',
            justifyContent: 'center',
            background: 'rgba(0, 0, 0, 0.4)',
            transition: 'all 0.3s ease',
            backdropFilter: 'blur(15px)',
          }}
        >
          <Typography
            color="primary.main"
            sx={{
              color: '#fff',
            }}
          >
            press Esc to cancel
          </Typography>
        </Alert>
      )}
    </>
  );
};
